import { Node, mergeAttributes, nodeInputRule } from '@tiptap/core';
import {
  NodeViewWrapper,
  ReactNodeViewRenderer,
  type NodeViewProps,
} from '@tiptap/react';
import { useEffect, useRef } from 'react';
import katex from 'katex';
import 'katex/dist/katex.min.css';

/**
 * KaTeX-rendered node view shared by the inline and display math nodes. Renders
 * into a ref via `katex.render` (never `dangerouslySetInnerHTML`), so the same
 * component drives the editor and the read-only view identically.
 *
 * @param {NodeViewProps} props - Tiptap node-view props.
 * @return {JSX.Element} The rendered math node.
 */
const MathNodeView = ({ node }: NodeViewProps) => {
  const ref = useRef<HTMLSpanElement>(null);
  const latex = String(node.attrs.latex ?? '');
  const displayMode = node.type.name === 'math_display';

  useEffect(() => {
    if (!ref.current) return;
    // `throwOnError: false` lets KaTeX render invalid/incomplete LaTeX (e.g. an
    // unbalanced brace typed mid-edit) inline in `errorColor` instead of
    // throwing. A raw throw here escapes the effect uncaught and tears down the
    // whole editor tree. The try/catch guards against any non-ParseError throw.
    try {
      katex.render(latex, ref.current, {
        displayMode,
        leqno: false,
        fleqn: false,
        throwOnError: false,
        errorColor: '#cc0000',
        strict: 'warn',
        output: 'htmlAndMathml',
        trust: false,
        macros: {
          '\\f': '#1f(#2)',
        },
      });
    } catch {
      ref.current.textContent = latex;
    }
  }, [latex, displayMode]);

  return (
    <NodeViewWrapper
      as={displayMode ? 'div' : 'span'}
      className="rich-text-math"
      data-latex={latex}
    >
      {/* Placeholder text keeps an empty formula selectable in the editor. */}
      <span ref={ref}>{latex ? null : '∅'}</span>
    </NodeViewWrapper>
  );
};

/** Shared attribute spec: a single `latex` string persisted to the doc JSON. */
const latexAttribute = {
  latex: {
    default: '',
    parseHTML: (element: HTMLElement) =>
      element.getAttribute('data-latex') ?? '',
    renderHTML: (attributes: { latex?: string }) => ({
      'data-latex': attributes.latex ?? '',
    }),
  },
};

/**
 * Inline math node (`math_inline`). Atom inline node carrying a `latex` attr;
 * name matches the backend `Tiptap::DocValidator` whitelist.
 */
export const MathInline = Node.create({
  name: 'math_inline',
  group: 'inline',
  inline: true,
  atom: true,
  selectable: true,

  addAttributes() {
    return latexAttribute;
  },

  parseHTML() {
    return [{ tag: 'span[data-type="math-inline"]' }];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'span',
      mergeAttributes(HTMLAttributes, { 'data-type': 'math-inline' }),
    ];
  },

  addNodeView() {
    // eslint-disable-next-line new-cap
    return ReactNodeViewRenderer(MathNodeView);
  },

  addInputRules() {
    return [
      // `$x$` → inline math (no nested `$`).
      nodeInputRule({
        find: /\$([^$]+)\$$/,
        type: this.type,
        getAttributes: (match) => ({ latex: match[1] }),
      }),
    ];
  },
});

/**
 * Block (display) math node (`math_display`). Atom block node carrying a
 * `latex` attr; name matches the backend `Tiptap::DocValidator` whitelist.
 */
export const MathDisplay = Node.create({
  name: 'math_display',
  group: 'block',
  atom: true,
  selectable: true,

  addAttributes() {
    return latexAttribute;
  },

  parseHTML() {
    return [{ tag: 'div[data-type="math-display"]' }];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'div',
      mergeAttributes(HTMLAttributes, { 'data-type': 'math-display' }),
    ];
  },

  addNodeView() {
    // eslint-disable-next-line new-cap
    return ReactNodeViewRenderer(MathNodeView);
  },

  addInputRules() {
    return [
      // `$$x$$` → display math.
      nodeInputRule({
        find: /\$\$([^$]+)\$\$$/,
        type: this.type,
        getAttributes: (match) => ({ latex: match[1] }),
      }),
    ];
  },
});
